import {
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { BehaviorSubject, Subject, Subscription, of } from 'rxjs';
import {
  auditTime,
  catchError,
  exhaustMap,
  filter,
  finalize,
  map,
  tap,
} from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { showIncomingNotificationToast } from '@app/services/notificaciones/notificacion-toast.util';
import { WebSocketService } from '@app/services/websocket/WebSocketService';
import { NotificacionesHttpService } from '@app/services/notificaciones/notificaciones-http.service';
import { NotificacionesHeaderService } from '@app/services/notificaciones/notificaciones-header.service';
import { Notificacion } from '@app/models/notificacion.model';

type Pestana = 'no-leidas' | 'leidas';

interface WsPayload {
  type?: string;
  notification?: any;
  notificationId?: number;
  userId?: number;
  count?: number;
}

@Component({
  selector: 'app-boton-notificaciones',
  templateUrl: './boton-notificaciones.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BotonNotificacionesComponent implements OnInit, OnDestroy {
  @Input() userId?: number;

  readonly unreadCount$ = new BehaviorSubject<number>(0);
  private readonly refreshCount$ = new Subject<void>();
  private subs = new Subscription();

  pestanaActiva: Pestana = 'no-leidas';
  cargando = false;

  private pageNoLeidas = 0;
  private pageLeidas = 0;
  private readonly size = 5;
  isLastPage = false;
  private readonly seen = new Set<string>();

  notificacionesMostradas: Notificacion[] = [];

  constructor(
    private ws: WebSocketService,
    private api: NotificacionesHttpService,
    private headerApi: NotificacionesHeaderService,
    private snack: MatSnackBar,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.resolverUserId();
    if (!this.userId) {
      this.unreadCount$.next(0);
      return;
    }

    this.subscribeToEventStreams(this.userId);
    this.configurarFallbackHTTP();

    // carga inicial contador
    this.refreshCount$.next();

    // menú inicial
    this.resetPaginacion('no-leidas');
    this.cargarPaginaActual(false);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private isDuplicate(e: WsPayload | any): boolean {
    const k = `${e?.type}:${e?.notificationId ?? e?.notification?.id ?? ''}:${
      e?.userId ?? ''
    }`;
    if (this.seen.has(k)) return true;
    this.seen.add(k);
    setTimeout(() => this.seen.delete(k), 2000);
    return false;
  }

  private resolverUserId(): void {
    if (this.userId) {
      return;
    }

    const rawId = localStorage.getItem('userId');
    if (rawId && !isNaN(+rawId)) {
      this.userId = +rawId;
      return;
    }
    try {
      const userStr =
        localStorage.getItem('user') ||
        localStorage.getItem('crm_user') ||
        '{}';
      const user = JSON.parse(userStr);
      if (user?.id) {
        this.userId = +user.id;
        return;
      }
    } catch (e) {
      console.error('Error parsing user from localStorage:', e);
    }
    if ((window as any).currentUser?.id) {
      this.userId = +(window as any).currentUser.id;
    }
  }

  private configurarFallbackHTTP(): void {
    this.subs.add(
      this.refreshCount$
        .pipe(
          auditTime(200),
          exhaustMap(() => {
            return this.headerApi.obtenerContadorNoLeidas().pipe(
              tap((count) => {
                this.unreadCount$.next(count ?? 0);
                this.cdr.detectChanges();
              }),
              // ⬇️ PARCHE: si falla, no llames al HttpService del listado
              catchError(() =>
                of(0).pipe(
                  tap((count) => {
                    this.unreadCount$.next(count);
                    this.cdr.detectChanges();
                  })
                )
              )
            );
          })
        )
        .subscribe()
    );
  }

  private subscribeToEventStreams(userId: number): void {
    // USER QUEUE - Suscribirse al tópico primero
    this.ws.subscribeToDynamicTopic(
      `/user/${userId}/queue/notifications`,
      'USER_NOTIFICATION'
    );

    this.subs.add(
      this.ws
        .getMessagesByDestination(`/user/${userId}/queue/notifications`)
        .pipe(filter((e) => !this.isDuplicate(e)))
        .subscribe((payload: WsPayload) => {
          if (typeof payload?.count === 'number') {
            this.zone.run(() => {
              this.unreadCount$.next(payload.count!);
              this.cdr.detectChanges();
            });
          } else {
            this.refreshCount$.next();
          }
          this.onIncoming('user', payload);
        })
    );

    // BROADCAST - Suscribirse al tópico primero
    this.ws.subscribeToDynamicTopic(
      `/topic/notifications/broadcast`,
      'BROADCAST_NOTIFICATION'
    );

    this.subs.add(
      this.ws
        .getMessagesByDestination(`/topic/notifications/broadcast`)
        .pipe(filter((e) => !this.isDuplicate(e)))
        .subscribe((payload: WsPayload) => {
          this.refreshCount$.next();
          this.onIncoming('broadcast', payload);
        })
    );

    // ADMIN - Suscribirse al tópico primero
    this.ws.subscribeToDynamicTopic(
      `/topic/notifications/admin`,
      'ADMIN_NOTIFICATION'
    );

    this.subs.add(
      this.ws
        .getMessagesByDestination(`/topic/notifications/admin`)
        .pipe(filter((e) => !this.isDuplicate(e)))
        .subscribe((payload: WsPayload) => {
          this.refreshCount$.next();
          this.onIncoming('admin', payload);
        })
    );
  }

  private onIncoming(
    channel: 'user' | 'broadcast' | 'admin' | string,
    raw: WsPayload
  ): void {
    const payload = raw || {};
    const type = String(
      payload?.type || (payload?.notification ? 'NEW' : '')
    ).toUpperCase();
    const notif = payload?.notification || null;
    const notifId = payload?.notificationId ?? notif?.id ?? null;
    const fromUserId = payload?.userId ?? null;
    const hasCount = typeof payload?.count === 'number';

    if (hasCount) {
      this.zone.run(() => {
        this.unreadCount$.next(payload.count!);
        this.cdr.detectChanges();
      });
    }

    switch (type) {
      case 'NEW': {
        showIncomingNotificationToast(payload as any, this.headerApi, () => {
          if (
            typeof (this.headerApi as any).triggerRefreshCount === 'function'
          ) {
            (this.headerApi as any).triggerRefreshCount();
          } else {
            this.refreshCount$.next();
          }
          // Refrescar también la lista de notificaciones del header
          this.cargarPaginaActual(false);
        });
        if (!hasCount) {
          this.refreshCount$.next();
        }
        // Refrescar también la lista de notificaciones del header
        this.cargarPaginaActual(false);
        break;
      }
      case 'COUNT': {
        if (!hasCount) this.refreshCount$.next();
        break;
      }
      case 'READ': {
        if (channel === 'broadcast') {
          if (
            fromUserId &&
            this.userId &&
            Number(fromUserId) !== Number(this.userId)
          ) {
            return; // lectura de otro usuario, no afecta mi contador
          }
        }
        if (!hasCount) {
          this.refreshCount$.next();
          // Refrescar también la lista de notificaciones del header
          this.cargarPaginaActual(false);
        }
        break;
      }
      case 'DELETED': {
        if (!hasCount) this.refreshCount$.next();
        if (notifId && Array.isArray(this.notificacionesMostradas)) {
          this.zone.run(() => {
            this.notificacionesMostradas = this.notificacionesMostradas.filter(
              (n) => n.id !== notifId
            );
            this.cdr.detectChanges();
          });
        }
        // Refrescar también la lista de notificaciones del header
        this.cargarPaginaActual(false);
        break;
      }
      default: {
        this.refreshCount$.next();
        break;
      }
    }
  }

  private actualizarContador(): void {
    this.refreshCount$.next();
  }

  abrirMenuNotificaciones(): void {
    if (this.notificacionesMostradas.length === 0) {
      this.resetPaginacion(this.pestanaActiva);
      this.cargarPaginaActual(false);
    }
  }

  cambiarPestana(p: Pestana): void {
    if (this.pestanaActiva === p) return;
    this.pestanaActiva = p;
    this.resetPaginacion(p);
    this.cargarPaginaActual(false);
  }

  cargarMasNotificaciones(): void {
    if (this.isLastPage || this.cargando) return;
    if (this.pestanaActiva === 'no-leidas') this.pageNoLeidas++;
    else this.pageLeidas++;
    this.cargarPaginaActual(true);
  }

  marcarComoLeida(n: Notificacion, ev?: MouseEvent): void {
    ev?.stopPropagation();
    this.headerApi.marcarComoLeida(n.id).subscribe({
      next: (ok) => {
        if (ok) {
          if (this.pestanaActiva === 'no-leidas') {
            this.notificacionesMostradas = this.notificacionesMostradas.filter(
              (x) => x.id !== n.id
            );
          }
          const currentCount = this.unreadCount$.value;
          if (currentCount > 0) this.unreadCount$.next(currentCount - 1);
          this.refreshCount$.next();
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.snack.open('Error al marcar como leída', 'OK', { duration: 2000 });
      },
    });
  }

  marcarTodoComoLeido(): void {
    if (!this.userId) return;
    this.headerApi.marcarTodasComoLeidas().subscribe({
      next: (ok) => {
        if (ok) {
          if (this.pestanaActiva === 'no-leidas') {
            this.notificacionesMostradas = [];
            this.isLastPage = true;
          }
          this.unreadCount$.next(0);
          this.snack.open('Todas marcadas como leídas', 'OK', {
            duration: 1800,
          });
          this.cdr.detectChanges();
          this.refreshCount$.next();
        }
      },
      error: () => {
        this.snack.open('Error al marcar todas como leídas', 'OK', {
          duration: 2000,
        });
      },
    });
  }

  verTodasLasNotificaciones(): void {
    this.snack.open('Ir a listado completo (implementa navegación)', 'OK', {
      duration: 1800,
    });
  }

  private resetPaginacion(p: Pestana) {
    this.isLastPage = false;
    if (p === 'no-leidas') this.pageNoLeidas = 0;
    else this.pageLeidas = 0;
    this.notificacionesMostradas = [];
  }

  private cargarPaginaActual(append: boolean): void {
    this.cargando = true;

    const page =
      this.pestanaActiva === 'no-leidas' ? this.pageNoLeidas : this.pageLeidas;

    // ⬇️ Usar los métodos del HeaderService correctos
    const source$ =
      this.pestanaActiva === 'no-leidas'
        ? this.headerApi.obtenerNoLeidasHeader(page, this.size)
        : this.headerApi.obtenerLeidasHeader(page, this.size);

    this.subs.add(
      source$
        .pipe(
          tap((resp) => {
            this.zone.run(() => {
              const content = resp?.content ?? [];
              this.isLastPage =
                typeof resp?.last === 'boolean'
                  ? resp.last
                  : page + 1 >= (resp?.totalPages || 0);

              this.notificacionesMostradas = append
                ? [...this.notificacionesMostradas, ...content]
                : content;
            });
          }),
          catchError((err) => {
            console.error('❌ Error al cargar notificaciones:', err);
            this.zone.run(() => {
              if (!append) this.notificacionesMostradas = [];
              this.isLastPage = true;
            });
            return of(null);
          }),
          finalize(() => {
            this.cargando = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe()
    );
  }

  // ------- binding helpers -------
  trackByNotificationId = (_: number, n: Notificacion) => n.id;

  getInitials(name: string): string {
    if (!name) return 'S';
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0])
      .join('')
      .toUpperCase();
  }

  getTipoBadgeClass(tipo?: string): string {
    const t = (tipo || '').toUpperCase();
    if (t === 'ADVERTENCIA' || t === 'ALERT' || t === 'WARNING')
      return 'bg-yellow-100 text-yellow-800';
    if (t === 'ERROR') return 'bg-red-100 text-red-700';
    if (t === 'ÉXITO' || t === 'EXITO' || t === 'SUCCESS')
      return 'bg-green-100 text-green-700';
    return 'bg-blue-100 text-blue-700';
  }

  formatearFecha(iso?: string): string {
    if (!iso) return '—';
    if (iso.includes('/')) {
      const [fecha, hora = '00:00:00'] = iso.split(' ');
      const [d, m, y] = fecha.split('/').map(Number);
      const [hh, mm, ss] = hora.split(':').map(Number);
      const dt = new Date(y, m - 1, d, hh || 0, mm || 0, ss || 0);
      return isNaN(+dt) ? '—' : dt.toLocaleString();
    }
    const d = new Date(iso);
    return isNaN(+d) ? '—' : d.toLocaleString();
  }

  getBadgeText(n: any): string | null {
    return n?.category ?? n?.type ?? null;
  }

  getBadgeClass(n: any): string {
    const t = (this.getBadgeText(n) || '').toString().toUpperCase();
    if (['ALERTA', 'URGENTE', 'WARNING'].includes(t)) {
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    }
    if (['INFO', 'GENERAL', 'BROADCAST', 'SISTEMA'].includes(t)) {
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    }
    if (['TAREA', 'ASIGNACION'].includes(t)) {
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    }
    return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
}
